import { useState, useContext, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/styles';
import { FilledInput, Button, FormGroup, FormLabel, FormControl, Box, Grid, Collapse, Divider, IconButton, Icon, Chip, Tooltip } from '@material-ui/core';
import { DataGrid } from '@material-ui/data-grid';

import DateRange from '../../common/date-range';

import AuthContext from '../../context';
import { get, post } from '../../utils/api-services';
import helpers from '../../utils/helpers';
import themeColors from '../../assets/colors';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Avoided from './avoided';
dayjs.extend(utc);

const useStyles = makeStyles(() => ({
    row: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between'
    },
    controls: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        padding: 0
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 20
    },
    storeBtn: {
        ...themeColors.blueBtn
    },
    searchBtn: {
        marginLeft: 20,
        ...themeColors.greenBtn
    },
    tableContainer: {
        width: '100%'
    },
}));

const WorkdayRequest = ({ parent, permissions }) => {

    const classes = useStyles();
    const { showToast, signOut, showLoader } = useContext(AuthContext);
    const isMounted = useRef(false);
    const [showModule, setShowModule] = useState(false);
    const [errorModal, setErrorModal] = useState({ open: false, data: {} });
    const hoursModule = (parent === 'hours');
    const [state, setState] = useState({
        data: [],
        form: {
            startDate: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
            endDate: dayjs().format('YYYY-MM-DD'),
            dateRange: `${dayjs().subtract(1, 'day').format('MM/DD/YYYY')} - ${dayjs().format('MM/DD/YYYY')}`
        },
        rangeModal: {
            open: false,
            range: [
                {
                    startDate: new Date(dayjs().subtract(1, 'day').toDate()),
                    endDate: new Date(),
                    key: 'main'
                }
            ]
        }
    });

    useEffect(() => {
        if (showModule && !isMounted.current) {
            makeRequest();
            isMounted.current = true;
        }
    }, [showModule]);

    const makeRequest = async () => {
        showLoader(true);
        const response = await get('/workdayRequest/search', { parent });
        if (response.status === 200) {
            setState(prev => ({ ...prev, data: response.data }));
        } else {
            console.log('[WorkdayRequest] makeRequest error', response);
            helpers.failRequest(response, showToast, signOut);
        }
        showLoader(false);
    }

    const handleModal = () => setState(prev => ({ ...prev, rangeModal: { ...prev.rangeModal, open: !prev.rangeModal.open } }));

    const handleDateChange = item => {
        const { startDate, endDate } = item.main;
        const dateRange = `${dayjs(startDate.toISOString()).format('MM/DD/YYYY')} - ${dayjs(endDate.toISOString()).format('MM/DD/YYYY')}`;
        setState(prev => ({
            ...prev,
            form: {
                ...prev.form,
                dateRange,
                startDate: dayjs(startDate.toISOString()).format('YYYY-MM-DD'),
                endDate: dayjs(endDate.toISOString()).format('YYYY-MM-DD')
            },
            rangeModal: { ...prev.rangeModal, range: [item.main] }
        }));
    }

    const validate = () => {
        const { startDate, endDate } = state.form;

        if (!permissions.create_attr) {
            showToast('You have no permissions for this action', 'error', 6500);
            return false;
        }

        const message = hoursModule ? `Are you sure you want to make a new request with this dates: start (${startDate}) end (${endDate})?` : 'Are you sure you want to make a new request?';
        const cond = window.confirm(message);

        return cond;
    }

    const store = async () => {
        const cond = validate();
        if (cond) {
            showLoader(true);
            const response = await post('/workdayRequest/store', { ...state.form, parent });
            if (response.status === 200) {
                setState(prev => ({ ...prev, data: response.data }));
                showToast(response.message);
            } else {
                console.log('[WorkdayRequest] store error', response);
                helpers.failRequest(response, showToast, signOut);
            }
            showLoader(false);
        }
    }

    const showErrorMsg = error => {
        const result = JSON.parse(error);

        setErrorModal({ open: true, data: result ?? {} });
    }

    const getLocalDate = ({ value }) => dayjs(value).utc().local().format('MM/DD/YYYY HH:mm:ss');
    const renderCell = ({ value }) => <Chip size='small' style={{ backgroundColor: themeColors.requestStatus[value] }} label={value} />;
    const getBoolean = ({ value }) => value ? '✓' : '✗';
    const getField = ({ value }) => value?.name ?? 'N/A';
    const renderError = ({ value }) => (value !== null) ? <Tooltip title='Show error message' placement='left'><IconButton onClick={() => showErrorMsg(value)}><Icon>{'visibility'}</Icon></IconButton></Tooltip> : <></>

    const hourColumns = [
        { field: 'date', headerName: 'Date', flex: 1, minWidth: 150, valueGetter: getLocalDate },
        { field: 'status', headerName: 'Status', flex: 1, minWidth: 100, renderCell },
        { field: 'start_date', headerName: 'Start date', flex: 1, minWidth: 130 },
        { field: 'end_date', headerName: 'End date', flex: 1, minWidth: 130 },
        { field: 'scheduled', headerName: 'Scheduled', flex: 1, minWidth: 130, valueGetter: getBoolean },
        { field: 'updated_at', headerName: 'Last update', flex: 1, minWidth: 150, valueGetter: getLocalDate },
        { field: 'user', headerName: 'User', flex: 1, minWidth: 150, valueGetter: getField },
        { field: 'sede', headerName: 'Sede', flex: 1, minWidth: 150, valueGetter: getField },
        { field: 'error_msg', headerName: 'Error msg', flex: 1, minWidth: 130, renderCell: renderError }
    ];

    const employeeColumns = [
        { field: 'date', headerName: 'Date', flex: 1, minWidth: 150, valueGetter: getLocalDate },
        { field: 'status', headerName: 'Status', flex: 1, minWidth: 100, renderCell },
        { field: 'scheduled', headerName: 'Scheduled', flex: 1, minWidth: 130, valueGetter: getBoolean },
        { field: 'updated_at', headerName: 'Last update', flex: 1, minWidth: 150, valueGetter: getLocalDate },
        { field: 'user', headerName: 'User', flex: 1, minWidth: 150, valueGetter: getField },
        { field: 'sede', headerName: 'Sede', flex: 1, minWidth: 150, valueGetter: getField },
        { field: 'error_msg', headerName: 'Error msg', flex: 1, minWidth: 130, renderCell: renderError }
    ];

    return (
        <>
            <Divider />

            <div className={classes.row}>
                <h2>{`Workday requests (${parent})`}</h2>
                <IconButton onClick={() => setShowModule(!showModule)}>
                    <Icon>{showModule ? 'expand_less' : 'expand_more'}</Icon>
                </IconButton>
            </div>

            <Collapse in={showModule}>
                <h3>{'Request form'}</h3>

                {
                    hoursModule &&
                    <Grid container>
                        <Grid item xs={12} md={6} lg={4}>
                            <FormGroup onClick={handleModal}>
                                <FormLabel>{'Date range'}</FormLabel>
                                <FormControl
                                    variant='filled'
                                    component={Box}
                                    width='100%'
                                >
                                    <FilledInput
                                        readOnly
                                        type='text'
                                        placeholder='Date range'
                                        value={state.form.dateRange}
                                    />
                                </FormControl>
                            </FormGroup>
                        </Grid>
                        <Grid item xs={6} md={3} lg={3}>
                            <FormGroup>
                                <FormLabel>{'Start date'}</FormLabel>
                                <FormControl
                                    variant='filled'
                                    component={Box}
                                    width='100%'
                                >
                                    <FilledInput
                                        disabled
                                        type='date'
                                        placeholder='Date'
                                        value={state.form.startDate}
                                    />
                                </FormControl>
                            </FormGroup>
                        </Grid>
                        <Grid item xs={6} md={3} lg={3}>
                            <FormGroup>
                                <FormLabel>{'End date'}</FormLabel>
                                <FormControl
                                    variant='filled'
                                    component={Box}
                                    width='100%'
                                >
                                    <FilledInput
                                        disabled
                                        type='date'
                                        placeholder='Date'
                                        value={state.form.endDate}
                                    />
                                </FormControl>
                            </FormGroup>
                        </Grid>
                    </Grid>
                }

                <Grid item xs={12} className={classes.controls}>
                    {
                        (permissions.create_attr) &&
                        <div className={classes.buttonContainer}>
                            <Button
                                variant='contained'
                                className={classes.storeBtn}
                                onClick={store}
                            >
                                {'Make new request'}
                            </Button>
                        </div>
                    }
                    <div className={classes.buttonContainer}>
                        <Button
                            variant='contained'
                            className={classes.searchBtn}
                            onClick={makeRequest}
                        >
                            {'Refresh'}
                        </Button>
                    </div>
                </Grid>

                <div className={classes.tableContainer}>
                    <DataGrid
                        rows={state.data}
                        columns={hoursModule ? hourColumns : employeeColumns}
                        pageSize={10}
                        density='compact'
                        autoHeight
                        getRowId={row => row.idworkday_request}
                    />
                </div>
            </Collapse>

            <DateRange
                open={state.rangeModal.open}
                range={state.rangeModal.range}
                maxDate={dayjs().toDate()}
                onClose={handleModal}
                onChange={handleDateChange}
            />

            <Avoided open={errorModal.open} onClose={() => setErrorModal({ open: false, data: {} })} data={errorModal.data} workday />
        </>
    );
}

export default WorkdayRequest;